/** 顶部工具栏：标题 / 撤销重做 / 缩放 / 保存状态 / 预览 / 发布。 */
import { useState } from 'react';
import { Button, Input, Select, Space, Tooltip, Typography, message, Modal } from 'antd';
import { UndoOutlined, RedoOutlined, SaveOutlined, EyeOutlined, CloudUploadOutlined, ZoomInOutlined, ZoomOutOutlined, PlusOutlined } from '@ant-design/icons';
import { selectCanRedo, selectCanUndo, useEditorStore } from '../store/store';
import { publish } from '../api/client';

const ZOOMS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
const STATUS_TEXT: Record<string, string> = { idle: '', dirty: '未保存', saving: '保存中…', saved: '已保存', error: '保存失败' };

interface Props { onSave(): Promise<void> }

export function Toolbar({ onSave }: Props) {
  const title = useEditorStore((s) => s.doc.title);
  const zoom = useEditorStore((s) => s.ui.zoom);
  const saveStatus = useEditorStore((s) => s.ui.saveStatus);
  const remoteId = useEditorStore((s) => s.ui.remoteId);
  const canUndo = useEditorStore(selectCanUndo);
  const canRedo = useEditorStore(selectCanRedo);
  const { setTitle, undo, redo, setZoom, setPreviewOpen } = useEditorStore.getState();
  const [publishing, setPublishing] = useState(false);

  const onPublish = async () => {
    if (!remoteId) { message.error('未连接服务端，无法发布'); return; }
    setPublishing(true);
    try {
      await onSave();
      const res = await publish(remoteId);
      Modal.success({
        title: '发布成功',
        content: (
          <div>
            <Typography.Paragraph copyable={{ text: res.url }}>{res.url}</Typography.Paragraph>
            <a href={res.url} target="_blank" rel="noreferrer">打开发布页</a>
          </div>
        ),
      });
    } catch (err) {
      message.error(`发布失败：${(err as Error).message}`);
    } finally {
      setPublishing(false);
    }
  };

  const zoomIdx = ZOOMS.findIndex((z) => z >= zoom);

  return (
    <div style={{ height: 48, display: 'flex', alignItems: 'center', padding: '0 12px', gap: 12, background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
      <span style={{ fontWeight: 600, color: '#1677ff' }}>H5</span>
      <Tooltip title="新建作品（新标签页，可选模板）">
        <Button type="text" icon={<PlusOutlined />} onClick={() => window.open(window.location.pathname, '_blank', 'noopener')} />
      </Tooltip>
      <Input variant="borderless" value={title} onChange={(e) => setTitle(e.target.value)} style={{ width: 220, fontWeight: 500 }} placeholder="作品标题" />
      <Space size={4}>
        <Tooltip title="撤销 (Ctrl+Z)"><Button type="text" icon={<UndoOutlined />} disabled={!canUndo} onClick={undo} /></Tooltip>
        <Tooltip title="重做 (Shift+Ctrl+Z)"><Button type="text" icon={<RedoOutlined />} disabled={!canRedo} onClick={redo} /></Tooltip>
      </Space>
      <Space size={4}>
        <Button type="text" icon={<ZoomOutOutlined />} onClick={() => setZoom(ZOOMS[Math.max(0, (zoomIdx < 0 ? ZOOMS.length : zoomIdx) - 1)]!)} />
        <Select size="small" style={{ width: 90 }} value={zoom} options={ZOOMS.map((z) => ({ label: `${Math.round(z * 100)}%`, value: z }))} onChange={setZoom} />
        <Button type="text" icon={<ZoomInOutlined />} onClick={() => setZoom(ZOOMS[Math.min(ZOOMS.length - 1, zoomIdx + 1)]!)} />
      </Space>
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 12, color: saveStatus === 'error' ? '#f5222d' : '#999' }}>
        {STATUS_TEXT[saveStatus]}{!remoteId && saveStatus !== 'idle' ? '（离线，仅本地）' : ''}
      </span>
      <Button icon={<SaveOutlined />} onClick={() => void onSave()} loading={saveStatus === 'saving'}>保存</Button>
      <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>预览</Button>
      <Button type="primary" icon={<CloudUploadOutlined />} loading={publishing} onClick={() => void onPublish()}>发布</Button>
    </div>
  );
}
